import { ArrowLeft, ArrowRight, Loader2, X } from 'lucide-react';
import { useOnboardingStore } from '../../store/onboardingStore';
import { StepIndicator } from './ui/step-indicator';
import { StepProductData } from './steps/step-1-product-data';
import { StepVisualContent } from './steps/step-2-visual-content';
import { StepAdditionalOffers } from './steps/step-3-additional-offers';
import { StepVisualStyle } from './steps/step-4-visual-style';
import { ProductCard } from './ui/product-card';
import { axiosInstanceConversationalEngine } from '../../services/instance';
import { useState, useEffect } from 'react';
import LoadingFluxi from '../common/LoadingFluxi/LoadingFluxi';
import { Toaster, toast } from 'react-hot-toast';
import { FunnelPayload } from '../../services/interface';
import { postFunnel } from '../../services/funnels';
import { dictionary } from '../../utils/dictionary';
import { useAuth } from '../../context/AuthContext';
import { dataFake } from './dataFake/dataFake';
import { useParams, useSearchParams } from 'react-router-dom';

// Add type definition at the top of the file
interface ScrapeResult {
  type: string;
  index: number;
  data: any;
  originalProduct: any;
}

interface ProductCompleteness {
  upsell: {
    enabled: boolean;
    products: any[];
  };
  bumpOffer?: {
    enabled: boolean;
    productName: string;
    price: string;
    description?: string;
    url_images?: string;
    image?: string;
    externalId?: string | null;
    providerId?: string | null;
    isManualEntry: boolean;
    productLink: string;
  };
}

// Custom toast notification function
const customToast = (message: string, title: string = 'Validation Error') => {
  return toast.custom(
    (t) => (
      <div
        className={`${t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
        style={{
          transition: 'all 0.5s ease-out',
          transform: t.visible ? 'translateX(0)' : 'translateX(100%)'
        }}
      >
        <div className="p-4 w-full flex items-center">
          <div className="flex-shrink-0">
            <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center">
              <X className="h-5 w-5 text-red-500" />
            </div>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-lg font-medium text-gray-900">
              {title}
            </p>
            <p className="mt-1 text-gray-600">
              {message}
            </p>
          </div>
        </div>
      </div>
    ),
    { duration: 4000 }
  );
};

export function OnboardingLayout() {
  const [searchParams] = useSearchParams();
  const uuidDataFake = searchParams.get('uuidData');
  const { data, nextStep, previousStep, updateProductData, updateVisualContent, updateAdditionalOffers, resetOnboardingData, salesAngles, contextData } = useOnboardingStore();
  const { language } = useAuth();
  const { step } = data;
  // const step = 3;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [productCompleteness, setProductCompleteness] = useState<ProductCompleteness | null>(data.additionalOffers);

  // Reset onboarding data when component mounts

  useEffect(() => {
    setProductCompleteness(data.additionalOffers);
  }, [data.additionalOffers]);

  useEffect(() => {
    resetOnboardingData();
  }, [resetOnboardingData]);

  const handleProductScrape = async () => {
    if (!data.productData.productLink) {
      return true;
    }

    setIsLoading(true);
    let success = false;

    try {
      // const response = await axiosInstanceConversationalEngine.post('/scrape-product', {
      //   product_url: data.productData.productLink
      // });

      const productData = dataFake[uuidDataFake as keyof typeof dataFake]?.productPrinpal as any;

      await new Promise(resolve => setTimeout(resolve, 3000));
      updateProductData({
        ...data.productData,
        name: productData.name,
        price: productData.external_sell_price.toString(),
        details: productData.description,
        isManualEntry: false,
        externalId: productData.external_id,
        providerId: productData.provider_id
      });

      // Update images
      if (productData.images && productData.images.length > 0) {
        updateVisualContent({
          images: productData.images
        });
      }

      // Validate required fields
      if (!productData.name || !productData.external_sell_price || !productData.description) {
        customToast('Please make sure the product has a name, price, description, and images.', 'Product Information Incomplete');
      } else {
        success = true; // Only mark success if we have all required data
      }
    } catch (err) {
      console.error('Error fetching product:', err);
      customToast('Failed to fetch product data. Please try again.');
      success = false;
    } finally {
      setIsLoading(false);
    }

    return success;
  };

  const handleNextStep = async () => {

    if (step === 1) {
      // Only run scraping if there's a product link AND product data is incomplete
      if (data.productData.productLink &&
        (!data.productData.name || !data.productData.price || !data.productData.details || !data.visualContent.images?.length)) {
        const scrapingSuccessful = await handleProductScrape();
        if (scrapingSuccessful) {
          nextStep();
        }
        return;
      }

      // Always validate required fields before proceeding
      if (!data.productData.name || !data.productData.price || !data.productData.details || (!data.visualContent.images?.length && !data.visualContent.imagesBase64?.length)) {
        customToast('Please make sure the product has a name, price, description, and images.', 'Product Information Incomplete');
        return; // Prevent proceeding to next step
      }

      nextStep();
    } else if (step === 2) {
      // Validate that either a customAngle is filled or a selectedAngle is selected
      if (!data.visualContent.customAngle && !data.visualContent.selectedAngle || data.visualContent.selectedAngle.length < 1) {
        customToast(dictionary["please-select-a-minimum-of-1-marketing-angle"][language], dictionary["marketing-angle-required"][language]);
        return; // Prevent proceeding to next step
      }

      nextStep();
    } else if (step === 3) {
      const bumpOfferEnabled = data.additionalOffers.bumpOffer?.enabled &&
        data.additionalOffers.bumpOffer?.productLink;

      const upsellEnabled = data.additionalOffers.upsell?.enabled &&
        data.additionalOffers.upsell.products?.some(p =>
          p.productLink || (p.productName && p.price && p.description && p.image)
        );


      if (bumpOfferEnabled && !upsellEnabled) {
        customToast('Please configure at least one upsell product.', 'Upsell Required');
        return; // Prevent proceeding to next step
      }

      const shouldScrape = (bumpOfferEnabled || upsellEnabled);

      setIsLoading(true);

      try {
        // Only execute scraping if shouldScrape is true
        // if (shouldScrape) {
        // Create promises for parallel execution
        // const promises = [];

        // Setup order bump scraping promise if needed
        // if (bumpOfferEnabled &&
        //   (!data.additionalOffers.bumpOffer?.isManualEntry ||
        //     (!data.additionalOffers.bumpOffer.productName &&
        //       !data.additionalOffers.bumpOffer.price &&
        //       !data.additionalOffers.bumpOffer.description))) {

        //   const bumpOfferPromise = axiosInstanceConversationalEngine.post('/scrape-product', {
        //     product_url: data.additionalOffers.bumpOffer?.productLink || ''
        //   }).then(response => {
        //     return { type: 'bumpOffer', data: response.data.data };
        //   });

        //   promises.push(bumpOfferPromise);
        // }

        // // Setup upsell products scraping promises if needed
        // if (upsellEnabled) {
        //   const upsellPromises = data.additionalOffers.upsell.products
        //     .filter(product => product.productLink && !product.isManualEntry)
        //     .map((product, index) => {
        //       return axiosInstanceConversationalEngine.post('/scrape-product', {
        //         product_url: product.productLink
        //       }).then(response => {
        //         return {
        //           type: 'upsell',
        //           index: index,
        //           originalProduct: product,
        //           data: response.data.data
        //         };
        //       });
        //     });

        //   promises.push(...upsellPromises);
        // }

        // Execute all scraping requests in parallel
        // const results = await Promise.all(promises);
        await new Promise(resolve => setTimeout(resolve, 3000));
        const results: ScrapeResult[] = [
          {
            type: 'bumpOffer',
            index: 0,
            originalProduct: data.additionalOffers.bumpOffer,
            data: dataFake[uuidDataFake as keyof typeof dataFake]?.productBumpOffer
          },
          {
            type: 'upsell',
            index: 0,
            originalProduct: data.additionalOffers.upsell.products[0],
            data: dataFake[uuidDataFake as keyof typeof dataFake]?.productUpsell
          }
        ];

        // Process the results
        const updatedAdditionalOffersNew = { ...data.additionalOffers };
        const updatedUpsellProducts = [...data.additionalOffers.upsell.products];

        results.forEach(result => {
          if (result.type === 'bumpOffer' && data.additionalOffers.bumpOffer) {
            const productData = result.data;
            updatedAdditionalOffersNew.bumpOffer = {
              ...data.additionalOffers.bumpOffer,
              productName: productData.name || '',
              price: productData.external_sell_price ? productData.external_sell_price.toString() : '0',
              description: productData.description || '',
              url_images: productData.images && productData.images.length > 0 ? productData.images[0] : '',
              image: '',
              externalId: productData.external_id || null,
              providerId: productData.provider_id || null,
              isManualEntry: false
            };
          } else if (result.type === 'upsell') {
            const productData = result.data;
            updatedUpsellProducts[result.index] = {
              ...result.originalProduct,
              productName: productData.name,
              price: productData.external_sell_price.toString(),
              description: productData.description,
              image: '',
              url_images: productData.images?.[0] ? [productData.images[0]] : [],
              externalId: productData.external_id || null,
              providerId: productData.provider_id || null,
              isManualEntry: false
            };
          }
        });

        // Update upsell products in the state
        if (upsellEnabled) {
          updatedAdditionalOffersNew.upsell = {
            ...data.additionalOffers.upsell,
            products: updatedUpsellProducts
          };
        }

        // Update the store with all scraped data
        handleProductCompleteness(updatedAdditionalOffersNew);

        if (updatedAdditionalOffersNew.bumpOffer?.enabled) {
          const bumpOffer = updatedAdditionalOffersNew.bumpOffer;
          if (!bumpOffer.productName || !bumpOffer.price || !bumpOffer.description || (!bumpOffer.image && !bumpOffer.url_images)) {
            customToast(dictionary["order-bump-information-incomplete"][language], dictionary["order-bump-information-incomplete-title"][language]);
            setIsLoading(false);
            return;
          }
        }

        if (updatedAdditionalOffersNew.upsell?.enabled && updatedAdditionalOffersNew.upsell.products?.length > 0) {
          for (const product of updatedAdditionalOffersNew.upsell?.products) {
            if (!product.productName || !product.price || !product.description || (!product.image && !((product.url_images ?? []).length > 0))) {
              customToast(dictionary["upsell-information-incomplete"][language], dictionary["upsell-information-incomplete-title"][language]);
              setIsLoading(false);
              return;
            }
          }
        }

        nextStep();
      } catch (err) {
        console.error('Error fetching product data:', err);
        customToast('Failed to fetch product data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    } else {
      nextStep();
    }
  };

  const handleProductCompleteness = (data: any) => {
    updateAdditionalOffers(data);
    setProductCompleteness(data);
  }

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return <StepProductData />;
      case 2:
        return <StepVisualContent />;
      case 3:
        return <StepAdditionalOffers />;
      case 4:
        return <StepVisualStyle />;
      default:
        return <StepProductData />;
    }
  };

  const isLastStep = step === 4;

  // NOTE: Product card is currently hidden but may be needed in the future
  const showProductCard = step > 1;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    // setIsLoading(true);

    await new Promise(resolve => setTimeout(resolve, 7000));

    window.location.href = window.location.origin + "/funnels"

  };

  const stepTitles = [dictionary["product-details"][language], dictionary["marketing-angle"][language], dictionary["upsell-options"][language], dictionary["brand-style"][language]];

  // Return loading screen directly when submitting
  // if (isSubmitting) {
  //   return <LoadingFluxi />;
  // }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <Toaster position="top-right" toastOptions={{
        duration: 4000,
        style: {
          background: '#fff',
          color: '#363636',
          boxShadow: '0 3px 10px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
          padding: '16px',
        },
      }} />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6 flex items-center justify-center gap-2">
          <img
            src="https://www.svgrepo.com/show/190530/magic-wand-wizard.svg"
            alt="Magic wand"
            className="w-7 h-7"
          />
          {dictionary["magical-funnel-creation"][language]}
        </h1>

        <div className="mb-6">
          {/* Step indicators with connector lines - full width layout */}
          <div className="w-full grid grid-cols-4">
            {[1, 2, 3, 4].map((stepNumber, index) => (
              <div key={stepNumber} className="flex flex-col items-center relative">
                <StepIndicator
                  step={stepNumber}
                  currentStep={step}
                  label={stepTitles[index]}
                />

                {/* Connector lines */}
                {index < 3 && (
                  <div className="absolute top-5 left-1/2 w-full h-0.5 z-0">
                    <div
                      className={`h-0.5 w-full ${stepNumber < step ? 'bg-primary-600' : 'bg-gray-300'}`}
                    ></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* NOTE: Product card is hidden but kept for future reference */}
        {showProductCard && (
          <div className="mb-6">
            <ProductCard />
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          {renderStepContent()}
        </div>

        <div className="flex justify-between">
          {step !== 1 && (
            <button
              type="button"
              onClick={previousStep}
              className="btn btn-secondary"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {dictionary["previous"][language]}
            </button>
          )}

          <button
            type="button"
            onClick={isLastStep ? handleSubmit : handleNextStep}
            className={`btn btn-primary ${step === 1 ? 'ml-auto' : ''}`}
          >
            {isLastStep ? (
              isSubmitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Create funnels'
              )
            ) : (
              <>
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {dictionary["next"][language]}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}